/**
 * StopCardWb — Werkbank-render van de stopkaart.
 *
 * Zelfde props en dezelfde hook (useStopCard) als de legacy StopCard; alleen
 * de presentatie verschilt. Opbouw van boven naar beneden:
 *   1. WbLabelStrip (nummer, naam, WB, type-vak), NIEUW-strook bij na-lock
 *   2. adres groot, tijdvenster mono, meta-regel (items, m², langste zijde, ETA)
 *   3. vlaggen: geld (rood), klantopmerking (oranje), klacht, op-factuur
 *   4. actiegrid NAVIGEER / BEL / FOTO (+ PIN/QR bij open bedrag), 52 px
 *   5. "Items (n)" klapt de itemlijst uit
 *   6. één grote primaire knop; Deels opgehaald en Overslaan als tekstlinks
 *   7. Bericht aan kantoor als tekstlink
 * Openstaand bedrag en klantopmerking zijn nooit ingeklapt (tokens.md).
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { MediaLightbox } from '../MediaLightbox';
import { useKleedFotos } from './useKleedFotos';
import { formatArrivalTime } from '../../lib/utils';
import { WbButton, WbFlag, WbLabelStrip } from '../wb';
import { useStopCard, vroegStand } from './useStopCard';
import { StopCardModals } from './StopCardModals';
import type { StopCardProps } from './types';

function euro(n: number): string {
  return `€${Math.abs(n).toFixed(2).replace('.', ',')}`;
}

function ActieTegel({ label, icon, onClick, disabled, accent }: {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  accent?: 'geld';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={clsx(
        'wb-focus min-h-tap flex items-center justify-center gap-2 rounded-wb border-wb font-wb-heading font-bold text-sm uppercase tracking-wide select-none disabled:opacity-40',
        accent === 'geld'
          ? 'bg-wb-geld border-wb-geldink text-wb-geldink active:bg-red-200'
          : 'bg-wb-vlak border-wb-rand text-wb-inkt active:bg-wb-gedempt'
      )}
    >
      <span className="w-5 h-5 shrink-0">{icon}</span>
      <span>{label}</span>
    </button>
  );
}

export function StopCardWb(props: StopCardProps) {
  const { stop, isNext, onSkip, onNavigate, onCall, isChauffeurMismatch, onRefreshRoute } = props;
  const s = useStopCard(props);

  const eta = formatArrivalTime(stop.Arrival_Time_D_t__c);

  // Afleverstop: thumbnail per kleed zodat de chauffeur ziet wat hij uit de
  // bus haalt. Bij ophalen zijn er nog geen wasserijfoto's.
  const fotoIds = s.isDeliveryStop ? s.displayItems.map((i) => i.itemId) : [];
  const kleedFotos = useKleedFotos(fotoIds);
  const alleFotos = s.displayItems.flatMap((i) => kleedFotos[i.itemId]?.fotos ?? []);
  const [lightbox, setLightbox] = useState<number | null>(null);
  const openFotoVan = (wiSfId: string) => {
    const thumbId = kleedFotos[wiSfId]?.thumbId;
    const idx = thumbId ? alleFotos.findIndex((f) => f.id === thumbId) : -1;
    setLightbox(idx >= 0 ? idx : 0);
  };
  const metaDelen: string[] = [];
  if (s.totalItemCount > 0) metaDelen.push(`${s.totalItemCount} ${s.totalItemCount === 1 ? 'item' : 'items'}`);
  if (stop.totalM2) metaDelen.push(`${stop.totalM2.toFixed(2).replace('.', ',')} m²`);
  if (s.longestSideCm > 0) metaDelen.push(`langste zijde ${(s.longestSideCm / 100).toFixed(2).replace('.', ',')} m`);

  const toonGeld = s.isDeliveryStop && s.hasPaymentInfo && !s.opFactuur && !s.effectivelyFullyPaid;
  const gridKolommen = toonGeld && !s.paymentSkipped ? 'grid-cols-2' : 'grid-cols-3';

  return (
    <div
      ref={s.cardRef}
      id={`stop-${stop.Id}`}
      data-stop-card
      data-completing={s.isCompleting}
      className={clsx(
        'relative overflow-hidden rounded-wb-lg border-wb bg-wb-vlak transition-all duration-300',
        isNext ? 'border-wb-mint' : 'border-wb-rand',
        s.isCompleting ? 'opacity-0 scale-95 -translate-x-4' : 'opacity-100 scale-100'
      )}
    >
      {/* NIEUW-strook: stop na de vergrendeling aangeplakt (zelfde semantiek als NieuweStopWaarschuwing) */}
      {stop.isAddedAfterLock && (
        <div className="bg-wb-mint text-wb-mintink px-3 py-1 font-wb-heading font-bold text-xs uppercase tracking-wide border-b-wb border-wb-mintink">
          Nieuw · later bijgekomen{stop.Route_Sequence__c == null ? ' · plek wordt berekend' : ''}
        </div>
      )}

      <WbLabelStrip
        nummer={stop.Route_Sequence__c ?? '?'}
        naam={s.displayCustomerName}
        sub={[s.displayWorkOrderName, s.displayContactName].filter(Boolean).join(' · ')}
        type={stop.Type__c}
        isNext={isNext}
      />

      <div className="px-3 py-3 space-y-3">
        {/* Adres + tijd + meta */}
        <div>
          <p className="text-lg font-semibold leading-snug text-wb-inkt whitespace-pre-line">{s.formattedAddress}</p>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 mt-1">
            {s.displayTimeWindow && <span className="font-mono text-base font-semibold text-wb-inkt">{s.displayTimeWindow}</span>}
            {eta && <span className="font-mono text-sm text-wb-inkt2">{props.tijdenLive ? 'verwacht' : 'ETA'} {eta}</span>}
            {stop.isSpoed && <span className="font-wb-heading font-bold text-xs uppercase text-wb-geldink">Spoed</span>}
            {isChauffeurMismatch && <span className="text-xs text-wb-inkt2">Andere chauffeur</span>}
          </div>
          {metaDelen.length > 0 && <p className="text-sm text-wb-inkt2 mt-0.5">{metaDelen.join(' · ')}</p>}
        </div>

        {/* Vlaggen — alleen als van toepassing, nooit ingeklapt */}
        {(() => {
          const vs = vroegStand(stop);
          if (!vs) return null;
          const nietBetaald = vs.soort === 'plek' && s.isDeliveryStop && s.hasPaymentInfo && !s.opFactuur && !s.effectivelyFullyPaid;
          return (
            <WbFlag kind={vs.soort === 'akkoord' || vs.soort === 'plek' ? 'info' : 'opmerking'} title={vs.tekst}>
              {nietBetaald ? 'Nog niet betaald: niet neerleggen, eerst innen.' : null}
            </WbFlag>
          );
        })()}
        {stop.isKlacht && (
          <WbFlag kind="geld" title="Klacht · herbehandeling">
            {stop.klachtReden
              ? `Reden: ${stop.klachtReden.split(';').map((r) => r.trim()).filter(Boolean).join(', ')}`
              : null}
          </WbFlag>
        )}
        {toonGeld && (
          <WbFlag
            kind="geld"
            title={s.localOpenAmount < 0 ? 'Te veel betaald' : `${euro(s.localOpenAmount)} nog te betalen`}
            onClick={s.localOpenAmount > 0 ? () => s.setShowPaymentModal(true) : undefined}
          >
            {s.localOpenAmount > 0
              ? `Pin of QR bij aflevering. Totaal ${euro(stop.totalInclBtw ?? 0)}, al betaald ${euro(s.localTotalTransactions)}.`
              : euro(s.localOpenAmount)}
          </WbFlag>
        )}
        {s.isDeliveryStop && s.opFactuur && (
          <WbFlag kind="info" title="Betaalt op factuur achteraf">
            Niet innen: geen pin, contant of QR vragen. Kantoor stuurt de factuur.
          </WbFlag>
        )}
        {s.isDeliveryStop && s.hasPaymentInfo && !s.opFactuur && s.effectivelyFullyPaid && (
          <p className="text-sm font-semibold text-wb-inkt2">Betaald · niets te innen</p>
        )}
        {stop.notes && (
          <WbFlag kind="opmerking" title={stop.Type__c === 'Ophalen' ? 'Opmerking ophalen' : 'Opmerking aflevering'}>
            {stop.notes}
          </WbFlag>
        )}
        {/* Vaste regel bij ophalen (Ilse, punt 119): losse spullen en etiketten
            komen in de wasserij niet gegarandeerd mee terug. Geen vlag, want
            vlaggen zijn alleen voor wat op deze stop van toepassing is. */}
        {s.isPickupStop && (
          <p className="text-sm text-wb-inkt2 leading-snug">
            Losse spullen (rol, spanbanden, klittenband) niet meenemen, tenzij de klant ze kwijt wil.
            Etiketten op het kleed blijven er niet altijd op; zeg dat bij twijfel.
          </p>
        )}

        {/* Actiegrid */}
        <div className={clsx('grid gap-2', gridKolommen)}>
          <ActieTegel
            label="Navigeer"
            onClick={onNavigate}
            icon={
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            }
          />
          <ActieTegel
            label="Bel"
            onClick={onCall}
            disabled={!s.displayPhoneNumber}
            icon={
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            }
          />
          <ActieTegel
            label="Foto"
            onClick={() => s.setShowCamera(true)}
            disabled={!stop.Werk_Order__c}
            icon={
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            }
          />
          {toonGeld && !s.paymentSkipped && (
            <ActieTegel
              label={stop.molliePaymentUrl ? 'Pin / QR' : 'Pin / contant'}
              accent="geld"
              onClick={() => s.setShowPaymentModal(true)}
              icon={
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              }
            />
          )}
        </div>

        {/* Betaling: QR apart en overslaan, alleen bij open bedrag */}
        {toonGeld && (
          <div className="flex items-center justify-between gap-2 -mt-1">
            {stop.molliePaymentUrl && !s.paymentSkipped ? (
              <WbButton variant="link" onClick={() => s.setShowQrModal(true)}>QR-code tonen</WbButton>
            ) : <span />}
            {s.paymentSkipped ? (
              <WbButton variant="link" onClick={() => s.setPaymentSkipped(false)}>Betaling later · wijzigen</WbButton>
            ) : (
              <WbButton variant="link" onClick={() => s.setPaymentSkipped(true)}>Betaling overslaan</WbButton>
            )}
          </div>
        )}

        {/* Items — ingeklapt, feiten staan al in de meta-regel; Foto's opent de lightbox direct */}
        <div className="flex items-center border-t-wb border-wb-gedempt pt-2 gap-2">
        {alleFotos.length > 0 && (
          <WbButton variant="link" onClick={() => setLightbox(0)} className="shrink-0">
            Foto's ({alleFotos.length})
          </WbButton>
        )}
        <button
          type="button"
          onClick={() => s.setIsExpanded(!s.isExpanded)}
          aria-expanded={s.isExpanded}
          className="wb-focus min-h-tap flex-1 flex items-center justify-between font-wb-heading font-bold text-base text-wb-inkt"
        >
          <span>
            Items{s.totalItemCount > 0 ? ` (${s.totalItemCount})` : ''}
            {s.isPickupStop && s.alreadyPickedUpCount > 0 && (
              <span className="font-body font-normal text-sm text-wb-inkt2"> · nog {s.toPickUpCount} op te halen</span>
            )}
            {s.isDeliveryStop && s.alreadyDeliveredCount > 0 && (
              <span className="font-body font-normal text-sm text-wb-inkt2"> · nog {s.toDeliverCount} af te leveren</span>
            )}
          </span>
          <svg className={clsx('w-5 h-5 transition-transform', s.isExpanded && 'rotate-180')} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        </div>

        {s.isExpanded && (
          <div className="space-y-2">
            {s.isPickupStop && (
              <Link
                to={`/workorder/${stop.Werk_Order__c}/add-item`}
                className="wb-focus min-h-tap flex items-center justify-center gap-2 w-full rounded-wb border-wb border-dashed border-wb-rand text-wb-inkt font-wb-heading font-bold text-sm uppercase tracking-wide"
              >
                + Vloerkleed toevoegen
              </Link>
            )}

            {s.isLoadingItems ? (
              <p className="text-sm text-wb-inkt2 py-2">Items laden…</p>
            ) : s.itemsError ? (
              <p className="text-sm text-wb-geldink py-2">{s.itemsError}</p>
            ) : s.items.length === 0 ? (
              <p className="text-sm text-wb-inkt2 py-2">Geen items gevonden</p>
            ) : (
              <>
                {s.displayItems.map((primaryItem, index) => {
                  const relatedExtras = s.items.filter(
                    (item) => item.parentItemId === primaryItem.itemId && item.productCategory === 'Extra'
                  );
                  const relatedPackages = s.items.filter(
                    (item) => item.parentItemId === primaryItem.itemId && item.productCategory === 'Package'
                  );
                  const isRound = primaryItem.isRoundRug === true || (primaryItem.diameterCm && primaryItem.diameterCm > 1);
                  const maat = primaryItem.oppervlakteBerekeningNodig === false
                    ? `${primaryItem.quantity ?? 1}×`
                    : isRound
                      ? `Ø ${primaryItem.diameterCm?.toString().replace('.', ',')} cm`
                      : primaryItem.lengthCm || primaryItem.widthCm
                        ? `${primaryItem.lengthCm?.toString().replace('.', ',')} × ${primaryItem.widthCm?.toString().replace('.', ',')} cm`
                        : 'Andere vorm';
                  const alOpgehaald = s.isPickupStop && !!primaryItem.status;
                  const alAfgeleverd = s.isDeliveryStop && primaryItem.status === 'Afgeleverd';
                  const prijs = primaryItem.totalPriceWithExtrasAndDiscount ?? primaryItem.totalPriceWithExtras ?? primaryItem.totalPrice;

                  return (
                    <div
                      key={primaryItem.itemId}
                      className={clsx('rounded-wb border-wb border-wb-gedempt px-3 py-2', (alOpgehaald || alAfgeleverd) && 'opacity-50')}
                    >
                      <div className="flex items-start justify-between gap-2">
                        {kleedFotos[primaryItem.itemId]?.thumb && (
                          <button type="button" onClick={() => openFotoVan(primaryItem.itemId)} className="wb-focus shrink-0 w-12 h-12 rounded-[4px] overflow-hidden border border-wb-rand" aria-label="Foto's">
                            <img src={kleedFotos[primaryItem.itemId]!.thumb!} alt="" className="w-full h-full object-cover" loading="lazy" />
                          </button>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] font-bold uppercase tracking-wide text-wb-inkt2">
                            {primaryItem.productName || 'Vloerkleed'} {index + 1}
                            {alOpgehaald && ' · al opgehaald'}
                            {alAfgeleverd && ' · al afgeleverd'}
                          </p>
                          <p className="font-mono text-base font-semibold text-wb-inkt">{primaryItem.itemName}</p>
                          <p className="text-sm text-wb-inkt2">
                            {maat}
                            {primaryItem.surfaceM2 && primaryItem.oppervlakteBerekeningNodig !== false
                              ? ` · ${primaryItem.surfaceM2.toFixed(2).replace('.', ',')} m²`
                              : ''}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          {prijs !== undefined && prijs > 0 && (
                            <p className="font-mono text-base font-semibold text-wb-inkt">€ {prijs.toFixed(2).replace('.', ',')}</p>
                          )}
                          {s.isPickupStop && !alOpgehaald && (
                            <button
                              type="button"
                              onClick={() => s.setEditingItem(primaryItem)}
                              className="wb-focus min-h-[40px] px-2 text-sm font-semibold underline underline-offset-4 text-wb-inkt2"
                            >
                              Bewerken
                            </button>
                          )}
                        </div>
                      </div>
                      {(relatedPackages.length > 0 || relatedExtras.length > 0) && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {relatedPackages.map((pkg) => (
                            <span key={pkg.itemId} className="text-xs font-bold px-2 py-0.5 rounded-[4px] bg-wb-inkt text-white">
                              {pkg.productName}
                            </span>
                          ))}
                          {relatedExtras.map((extra) => (
                            <span key={extra.itemId} className="text-xs font-semibold px-2 py-0.5 rounded-[4px] border border-wb-rand text-wb-inkt">
                              {extra.productName}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}

                {s.items.some((i) => (i.productCategory === 'Extra' || i.productCategory === 'Package') && !i.parentItemId) && (
                  <div className="flex flex-wrap gap-1">
                    {s.items
                      .filter((i) => (i.productCategory === 'Extra' || i.productCategory === 'Package') && !i.parentItemId)
                      .map((i) => (
                        <span key={i.itemId} className="text-xs font-semibold px-2 py-0.5 rounded-[4px] border border-wb-rand text-wb-inkt">
                          {i.productName || i.itemName}
                        </span>
                      ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Primaire knop — pas na afgehandelde betaling bij een afleverstop met geld */}
        {s.canShowCompleteActions ? (
          <div className="space-y-1 pt-1">
            <WbButton
              size="lg"
              onClick={s.handleCompleteWithAnimation}
              disabled={isChauffeurMismatch || s.isCompleting}
              isLoading={s.isCompleting}
            >
              {s.primaryLabel}
            </WbButton>
            <div className="flex items-center justify-between">
              {s.canPartialPickup ? (
                <WbButton variant="link" onClick={s.openPartialModal} disabled={s.isCompleting}>
                  {s.partialRichting === 'afleveren' ? 'Kleed niet afgeleverd? Deels afgeleverd' : 'Kleed niet meegekregen? Deels opgehaald'}
                </WbButton>
              ) : <span />}
              <WbButton variant="link" onClick={onSkip} disabled={s.isCompleting}>
                Overslaan
              </WbButton>
            </div>
          </div>
        ) : (
          <p className="text-sm text-wb-inkt2 pt-1">Handel eerst de betaling af (of sla die over) om af te ronden.</p>
        )}

        <div className="border-t-wb border-wb-gedempt pt-1 -mb-1">
          <WbButton variant="link" onClick={() => s.setShowMessageModal(true)}>Bericht aan kantoor</WbButton>
        </div>
      </div>

      <StopCardModals stop={stop} onRefreshRoute={onRefreshRoute} s={s} />
      {lightbox !== null && alleFotos.length > 0 && (
        <MediaLightbox fotos={alleFotos} start={lightbox} onClose={() => setLightbox(null)} />
      )}
    </div>
  );
}
